import { ChangeEventHandler, FC, TextareaHTMLAttributes, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldBaseProps } from 'core/types/field';

import FieldWrapper from 'shared/inputs/FieldWrapper';

import styles from './styles.module.scss';

type TextAreaProps = FieldBaseProps<
	Pick<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'placeholder' | 'disabled' | 'rows' | 'cols'> & {
		value?: string | number;
		// eslint-disable-next-line no-unused-vars
		onChange?: (value: string | number) => void;
		isPencilHidden?: boolean;
		isRequired?: boolean;
	}
>;

const TextArea: FC<TextAreaProps> = ({
	value,
	onChange,
	id,
	placeholder,
	rows = 4,
	cols,
	isPencilHidden,
	...fieldBaseProps
}) => {
	useTranslation();

	const { field, disabled } = fieldBaseProps;

	const textareaValue = useMemo(() => value || (field.value as typeof value), [value, field]);

	const textareaChangeHandler: ChangeEventHandler<HTMLTextAreaElement> = e => {
		if (onChange) {
			onChange(e.target.value);
			return;
		}

		field.onChange(e);
	};

	const isPencilVisible = field.value && !disabled && !isPencilHidden;

	return (
		<FieldWrapper {...fieldBaseProps}>
			<div className={styles.container}>
				<textarea
					className={styles.textarea}
					{...field}
					id={id || field.name}
					value={textareaValue}
					onChange={textareaChangeHandler}
					placeholder={placeholder}
					disabled={disabled}
					rows={rows}
					cols={cols}
				/>
				{isPencilVisible ? <label className={styles.edit} htmlFor={field.name} /> : null}
			</div>
		</FieldWrapper>
	);
};

export default TextArea;
